import { useEffect, useState } from "react";
import { NavLink, Outlet, Navigate, useNavigate, Link } from "react-router-dom";
import { Home, FolderOpen, PlusCircle, Library, Target, User, Menu, X, LogOut } from "lucide-react";
import { useAuth } from "./AuthContext";
import { Spinner } from "./ui";
import api from "./api";

const nav = [
  { to: "/app", label: "Home", icon: Home, end: true },
  { to: "/app/cases", label: "Cases", icon: FolderOpen },
  { to: "/app/new", label: "Open New Case", icon: PlusCircle },
  { to: "/app/archive", label: "Archive", icon: Library },
  { to: "/app/profile", label: "Decision Profile", icon: Target },
  { to: "/app/account", label: "Account", icon: User },
];

export default function AppShell() {
  const { user, ready, logout } = useAuth();
  const [open, setOpen] = useState(false);
  const [caseCount, setCaseCount] = useState(0);
  const navigate = useNavigate();

  useEffect(() => {
    if (!user) return;
    api.get("/cases").then((r) => setCaseCount(r.data.cases?.length || 0)).catch(() => {});
  }, [user]);

  if (!ready) return <div className="min-h-screen bg-[#1F1E1B] flex items-center justify-center"><Spinner label="Opening the boardroom…" /></div>;
  if (!user) return <Navigate to="/auth" replace />;

  const signOut = () => {
    logout();
    navigate("/auth");
  };

  const sidebar = (
    <div className="flex flex-col h-full">
      <Link to="/" data-testid="shell-wordmark" className="font-serif-display text-2xl tracking-tight text-white px-6 py-7 block">
        Boardroom<sup className="font-mono-label text-[9px] ml-1">®</sup>
      </Link>
      <nav className="flex-1 px-3 space-y-1">
        {nav.map(({ to, label, icon: Icon, end }) => (
          <NavLink
            key={to}
            to={to}
            end={end}
            data-testid={`nav-${label.toLowerCase().replace(/\s+/g, "-")}`}
            onClick={() => setOpen(false)}
            className={({ isActive }) =>
              `flex items-center gap-3 px-4 py-3 rounded-xl font-mono-label text-[10px] tracking-[0.2em] uppercase transition-colors duration-300 ${
                isActive ? "bg-[#B5A58A]/10 text-[#B5A58A]" : "text-stone-400 hover:text-white hover:bg-white/[0.04]"
              }`
            }
          >
            <Icon className="w-4 h-4" strokeWidth={1.5} />
            {label}
          </NavLink>
        ))}
      </nav>
      <button
        data-testid="nav-sign-out"
        onClick={signOut}
        className="flex items-center gap-3 px-7 py-6 font-mono-label text-[10px] tracking-[0.2em] uppercase text-stone-500 hover:text-white transition-colors duration-300"
      >
        <LogOut className="w-4 h-4" strokeWidth={1.5} />
        Sign out
      </button>
    </div>
  );

  return (
    <div data-testid="app-shell" className="min-h-screen bg-[#1F1E1B] text-[#F2EFE9]">
      <aside className="hidden lg:block fixed inset-y-0 left-0 w-64 bg-[#26241F] border-r border-white/[0.06] z-40">
        {sidebar}
      </aside>
      {open && (
        <div className="lg:hidden fixed inset-0 z-50 bg-black/60" onClick={() => setOpen(false)}>
          <aside className="w-72 h-full bg-[#26241F] border-r border-white/[0.06]" onClick={(e) => e.stopPropagation()}>
            {sidebar}
          </aside>
        </div>
      )}

      <div className="lg:pl-64">
        <header className="sticky top-0 z-30 bg-[#1F1E1B]/90 backdrop-blur-md border-b border-white/[0.06] flex items-center justify-between px-5 md:px-10 py-4">
          <div className="flex items-center gap-4">
            <button data-testid="mobile-menu-btn" className="lg:hidden text-stone-300" onClick={() => setOpen(true)}>
              <Menu className="w-5 h-5" strokeWidth={1.5} />
            </button>
            <p className="font-mono-label text-[9px] tracking-[0.3em] uppercase text-stone-500 hidden sm:block">
              We do not have chats. We build cases.
            </p>
          </div>
          <div className="flex items-center gap-4">
            <span data-testid="case-counter" className="font-mono-label text-[9px] tracking-[0.2em] uppercase text-stone-500">
              {caseCount} {caseCount === 1 ? "case" : "cases"}
            </span>
            <Link
              to="/app/account"
              data-testid="user-avatar"
              className="w-9 h-9 rounded-full bg-[#B5A58A]/15 border border-[#B5A58A]/30 flex items-center justify-center font-serif-display text-sm text-[#B5A58A]"
            >
              {(user.name || user.email || "G")[0].toUpperCase()}
            </Link>
          </div>
        </header>

        {user.is_guest && (
          <div
            data-testid="guest-ribbon"
            className="bg-[#B37D3B]/10 border-b border-[#B37D3B]/30 px-5 md:px-10 py-2.5 flex flex-wrap items-center justify-between gap-2"
          >
            <p className="font-mono-label text-[9px] tracking-[0.2em] uppercase text-[#D09B5F]">
              Temporary — expires in 24h unless you create an account
            </p>
            <Link to="/auth?mode=register" data-testid="guest-create-account" className="font-mono-label text-[9px] tracking-[0.2em] uppercase text-white border-b border-[#B5A58A]/60 pb-0.5">
              Create account
            </Link>
          </div>
        )}

        <main className="px-5 md:px-10 py-8 md:py-12 max-w-6xl">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
